/**
 * `cart-notification.js`
 *
 * Handles opening, closing, and re-rendering of the cart notification (drawer).
 * This version makes sure that if Shopify’s AJAX response includes a "cart-icon-bubble"
 * section, we forcibly remove it so the icon never gets overwritten (and hidden).
 */

class CartNotification extends HTMLElement {
  constructor() {
    super();

    // The <div id="cart-notification">…</div> wrapper inside Dawn
    this.notification = document.getElementById('cart-notification');
    // If you have a <sticky-header> element, this.reveal() will put it back into view
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    // Close on Escape key:
    this.notification.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    // “X” / close-drawer buttons inside the notification:
    this.querySelectorAll('button[type="button"]').forEach((closeButton) => {
      closeButton.addEventListener('click', this.close.bind(this));
    });
  }

  open() {
    // Add the classes that slide the drawer in (Dawn’s CSS uses `.active`)
    this.notification.classList.add('animate', 'active');

    // After the CSS transition ends, focus + trap focus
    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        if (typeof trapFocus === 'function') {
          trapFocus(this.notification);
        }
      },
      { once: true }
    );

    // Any click outside the drawer should close it
    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    // Remove “active” so the drawer slides closed
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    // Un-trap focus if needed
    if (typeof removeTrapFocus === 'function' && this.activeElement) {
      removeTrapFocus(this.activeElement);
    }

    // Immediately restore the cart icon in case Shopify’s JS added “hidden” or inline styles
    const cartIcon = document.querySelector('#cart-icon-bubble');
    if (cartIcon) {
      cartIcon.classList.remove('hidden');
      cartIcon.style.display = 'inline-flex';
      cartIcon.style.visibility = 'visible';
      cartIcon.style.opacity = '1';
    }
  }

  /**
   * Re-renders only the “product” and “buttons” inside the drawer.
   * If Shopify’s AJAX response also included a “cart-icon-bubble” section,
   * we delete it here so that Dawn never uses it to overwrite your actual icon.
   */
  renderContents(parsedState) {
    // parsedState.key is the newly-added line item’s key
    this.cartItemKey = parsedState.key;

    // ===== NEW: remove any “cart-icon-bubble” HTML from parsedState.sections:
    if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      delete parsedState.sections['cart-icon-bubble'];
    }
    // ===== END NEW BLOCK

    // For each section_id that we explicitly want to replace inside the drawer:
    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;

      // parsedState.sections[section.id] is the raw HTML string for that section
      const html = parsedState.sections[section.id];
      container.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    // If there is a sticky header, reveal it again before re-opening
    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        // Matches #cart-notification-product-<ITEM_KEY>
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      // Notice: we do NOT return {id: 'cart-icon-bubble'} here,
      // so Dawn’s default code will never re-render that element.
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
